import { useEffect, useMemo, useState } from "react";
import { Icon, Button } from "../components/ui";
import { familyService } from "../services/familyService";
import { fileUrl } from "../config/api";
import { useFamilyAuth } from "../store/familyAuthStore";

const centreFactsFromFamily = (family = {}) => {
  const centreName = family.centre_suburb || family.centre_name || family.centre;
  return [
    centreName && { k: "Your centre", v: centreName },
    family.centre_hours && { k: "Centre hours", v: family.centre_hours },
    family.centre_manager && { k: "Director", v: family.centre_manager },
  ].filter(Boolean);
};

const isWelcomeChapter = (chapter, index) => {
  const no = String(chapter?.chapter_no || "").replace(/^0+/, "") || String(index + 1);
  return no === "1";
};

const looksLikeHtml = (value) => /<[a-z][\s\S]*>/i.test(String(value || ""));

function HandbookBody({ body }) {
  const text = String(body || "");
  if (looksLikeHtml(text)) {
    return <div className="hb-body" dangerouslySetInnerHTML={{ __html: text }} />;
  }
  return (
    <div className="hb-body">
      {text.split(/\n+/).filter(Boolean).map((p, i) => <p key={i}>{p}</p>)}
    </div>
  );
}

export default function Handbook() {
  const family = useFamilyAuth((s) => s.family);
  const [chapters, setChapters] = useState([]);
  const [active, setActive] = useState(0);
  const [settings, setSettings] = useState({});
  const centreFacts = useMemo(() => centreFactsFromFamily(family), [family]);

  useEffect(() => {
    familyService.getHandbook().then((r) => setChapters(r?.data || [])).catch(() => {});
    familyService.getPortalSettings().then((r) => setSettings(r?.data || {})).catch(() => {});
  }, []);

  if (chapters.length === 0) {
    return (
      <div className="page page--narrow">
        <div className="card policies-card">
          <span className="icon-circle lg amber"><Icon name="book" /></span>
          <p className="eyebrow mt-24">Parent Handbook</p>
          <h1 className="mt-8">Your handbook is on its way</h1>
          <p className="lead">
            Handbook chapters will appear here as soon as your centre publishes them.
          </p>
        </div>
      </div>
    );
  }

  const ch = chapters[active] || chapters[0];
  const total = String(chapters.length).padStart(2, "0");
  const chapterFacts = Array.isArray(ch.facts) ? ch.facts : [];
  const facts = isWelcomeChapter(ch, active) && centreFacts.length > 0
    ? centreFacts
    : chapterFacts;
  const pdf = settings.handbook_pdf ? fileUrl(settings.handbook_pdf) : null;

  return (
    <div className="workspace">
      <aside className="ws-side">
        <p className="eyebrow mb-18">Parent Handbook</p>
        <nav className="toc">
          {chapters.map((c, i) => (
            <button key={c.id} className={"toc-item" + (i === active ? " is-active" : "")} onClick={() => setActive(i)}>
              <span className="toc-n">{c.chapter_no || String(i + 1).padStart(2, "0")}</span>
              <span className="toc-t">{c.title}</span>
            </button>
          ))}
        </nav>
      </aside>

      <div className="ws-main scroll-area">
        <div className="ws-main-inner">
          <div className="hb-top">
            <p className="eyebrow muted">Chapter {ch.chapter_no || String(active + 1).padStart(2, "0")} of {total}</p>
            {pdf
              ? <a href={pdf} target="_blank" rel="noreferrer"><Button variant="primary" size="sm" icon="download">Download PDF</Button></a>
              : <Button variant="primary" size="sm" icon="download" disabled>Download PDF</Button>}
          </div>

          <div className="hb-numberline">
            <span className="hb-bignum">{ch.chapter_no || String(active + 1).padStart(2, "0")}</span>
            <h1>{ch.title}</h1>
          </div>

          <HandbookBody body={ch.body} />

          {facts.length > 0 && (
            <div className="hb-facts">
              {facts.map((f, i) => (
                <div className="fact" key={i}>
                  <div className="fact-k">{f.k}</div>
                  <div className="fact-v">{f.v}</div>
                </div>
              ))}
            </div>
          )}

          <div className="hb-nav">
            <button className="hb-navbtn" disabled={active === 0} onClick={() => setActive((a) => Math.max(0, a - 1))}>
              <Icon name="arrowLeft" className="ico-16" /> Previous
            </button>
            <button className="hb-navbtn" disabled={active === chapters.length - 1} onClick={() => setActive((a) => Math.min(chapters.length - 1, a + 1))}>
              Next chapter <Icon name="chevronRight" className="ico-16" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
